import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { StudentConectaTag } from '@/components/ui/custom/ConectaTag';
import { Avatar } from '@/components/ui/custom/dataDisplay/Avatar';
import NameAge from '@/components/ui/custom/dataDisplay/NameAge';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Skeleton from '@/components/ui/custom/Skeleton';
import { StudentReportStatus } from '@/components/scheduledLessonReport/StudentReport';
import useStatisticService from '@/data/hook/useStatistics';
import { scheduledLessonStatisticsQueryKeys } from '@/data/services/querykeys';
import { handleUserFullName } from '@/functions/handleUserFullName';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import Klass from '@/models/Klass';
import attendancePercentage from '@/utils/statistics/attendencePercentage';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';

interface KlassesStudentInfoProps {
  enrollment: Enrollment;
  klass: Klass;
}

export default function KlassesStudentInfo(props: KlassesStudentInfoProps) {
  const { enrollment, klass } = props;
  const student = enrollment.student;

  const { t } = useTranslation('translation', {
    keyPrefix: 'klassProgressPage',
  });

  const { statistic: studentStatistic, isInitialLoading: isLoading } =
    useStatisticService(
      scheduledLessonStatisticsQueryKeys.list({
        klassId: klass.id,
        studentId: student.id,
      }),
    );

  const studentUrl = `/admin/students/info/?${createSearchParams({
    search: handleUserFullName(student),
  })}`;
  const navigate = useNavigate();
  const handleOnClick = () => navigate(studentUrl);
  const loading = (
    <Skeleton className="h-4 w-4 rounded-sm bg-primary-content" />
  );
  const percentage = attendancePercentage({
    presences: studentStatistic?.presencesCount || 0,
    totalReports:
      (studentStatistic?.presencesCount || 0) +
      (studentStatistic?.absencesCount || 0),
  }).toFixed(0);

  const grayscale =
    enrollment.status !== EnrollmentStatusEnum.ACTIVE
      ? 'grayscale-[80] opacity-30'
      : '';

  return (
    <div className="flex w-fit h-fit border-b border-neutral-content">
      <div
        className={`flex flex-col gap-2.5 w-48 py-3 h-20 justify-center ${grayscale}`}
      >
        <div
          onClick={handleOnClick}
          className="relative flex items-center gap-2.5 cursor-pointer"
        >
          <Avatar userId={student.id} size="7" />
          <NameAge
            user={student}
            className={{
              base: 'flex flex-col gap-1 truncate w-full',
              text: 'underline w-full shrink !text-primary break-words text-14 font-rubik font-500 truncate',
            }}
          />
          <StudentConectaTag studentType={student.studentType} />
          <ConditionalRenderer
            condition={
              enrollment.status === EnrollmentStatusEnum.AWAITING_PAYMENT
            }
          >
            <StudentReportStatus
              status={enrollment.status}
              className="left-6"
            />
          </ConditionalRenderer>
        </div>
        <div className="flex items-center justify-between text-14">
          <div className="flex items-center gap-1">
            <Text text={`${t('attendances')}:`} />
            <ConditionalRenderer condition={!isLoading} fallback={loading}>
              <Text
                testId="studentLessonCount"
                text={studentStatistic?.presencesCount || 0}
                className="text-primary"
              />
            </ConditionalRenderer>
          </div>
          <div className="flex items-center gap-1">
            <Text text={`${t('absences')}:`} />
            <ConditionalRenderer condition={!isLoading} fallback={loading}>
              <Text
                testId="studentMissedCount"
                text={studentStatistic?.absencesCount || 0}
                className="text-primary"
              />
            </ConditionalRenderer>
          </div>
          <div className="flex items-center gap-1">
            <Text text="PP:" />
            <ConditionalRenderer condition={!isLoading} fallback={loading}>
              <Text
                testId="studentPP"
                text={`${percentage || 0}%`}
                className="text-primary"
              />
            </ConditionalRenderer>
          </div>
        </div>
      </div>
    </div>
  );
}
